import { FormApi } from "final-form";
import React from "react";
import { Form } from "react-final-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "src/store";
import { PrimaryButton, SecondaryButton } from "src/ui/shared/CustomButton";
import { Modal } from "src/ui/shared/Modal/Modal";
import { makeStyles } from "tss-react/mui";

import { selectGroups } from "@dashboard/groups/store/selectors";
import { Box, Theme } from "@mui/material";
import { CheckboxField } from "@shared/form/CheckboxField";

import { Group } from "../../devices/types";

import { GroupWithChildren } from "./GroupFilterHeader/GroupFilterHeader";
import { applyHierarchyCheckboxToggle, buildGroupTree, getAllGroupIds } from "./GroupFilterHeader/GroupFilterHeader.utils";
import { GroupFilterItem } from "./GroupFilterHeader/GroupFilterItem";

const removeFirstCharFromKeys = (obj: Record<string, boolean>): Record<string, boolean> => {
    const newObj: Record<string, boolean> = {};
    for (const key in obj) {
        const newKey = key.slice(1);
        newObj[newKey] = obj[key];
    }
    return newObj;
};

const hasExpandableGroups = (groups: GroupWithChildren[]): boolean => {
    return groups.some((group) => group.children.length > 0 || hasExpandableGroups(group.children));
};

const useStyles = makeStyles()((theme: Theme) => ({
    container: {
        margin: theme.spacing(3, 0),
        background: theme.palette.emteria.background.paper,
        borderRadius: theme.spacing(1),
        padding: theme.spacing(2),
    },
    content: {
        background: theme.palette.emteria.background.tableCells,
        borderRadius: theme.spacing(1),
        display: "flex",
        flexDirection: "column",
        position: "relative",
    },
}));

type FormValues = {
    [key: string]: boolean;
};

const createHierarchyToggle = (allGroups: Group[], form: FormApi<FormValues>) => {
    return (group: GroupWithChildren) => {
        const values = form.getState().values as Record<string, boolean | undefined> | undefined;
        const setCheckbox = (name: string, value: boolean) => {
            form.change(name, value);
        };
        applyHierarchyCheckboxToggle(allGroups, group, values, setCheckbox);
    };
};

type Props = {
    open: boolean;
    initialValues: FormValues;
    setOpen: React.Dispatch<React.SetStateAction<boolean>>;
    onSubmit: (values: FormValues) => void;
};

export const GroupFilterModal = (props: Props) => {
    const [expandedGroups, setExpandedGroups] = React.useState<number[]>([]);
    const { classes } = useStyles();
    const { t } = useTranslation(["modals", "common"]);

    const allGroups = useSelector(selectGroups);
    const groups = buildGroupTree(allGroups);
    const hasExpandable = hasExpandableGroups(groups);

    const truthyFilters = allGroups.reduce((acc, group) => ({ ...acc, [`$${group.id}`]: true }), {});
    const falsyFilters = allGroups.reduce((acc, group) => ({ ...acc, [`$${group.id}`]: false }), {});

    const onSubmit = (values: FormValues) => {
        props.onSubmit(removeFirstCharFromKeys(values));
        props.setOpen(false);
    };

    const handleCloseButton = (form: FormApi<FormValues>) => {
        form.reset(props.initialValues);
        props.setOpen(false);
    };

    const onExpand = (groupId: number) => {
        setExpandedGroups((prev) => (prev.includes(groupId) ? prev.filter((id) => id !== groupId) : [...prev, groupId]));
    };
    const onCollapseAllChange = () => {
        const allExpanded = groups.every((group) => expandedGroups.includes(group.id));
        if (allExpanded) {
            setExpandedGroups([]);
        } else {
            const allGroupIds = getAllGroupIds(groups);
            setExpandedGroups(allGroupIds);
        }
    };
    const onSelectAll = (checked: boolean, form: FormApi<FormValues>) => {
        form.reset(checked ? truthyFilters : falsyFilters);
    };

    return (
        <Form<FormValues>
            onSubmit={onSubmit}
            initialValues={props.initialValues}
            render={({ form, handleSubmit, values }) => (
                <form onSubmit={handleSubmit}>
                    <Modal
                        open={props.open}
                        title={t("modals:groupFilter.title")}
                        onClose={() => handleCloseButton(form)}
                        endButton={<PrimaryButton onClick={form.submit}>{t("common:button.confirm")}</PrimaryButton>}
                        startButton={
                            hasExpandable ? (
                                <SecondaryButton onClick={onCollapseAllChange}>
                                    {expandedGroups.length ? t("common:button.collapseAll") : t("common:button.expandAll")}
                                </SecondaryButton>
                            ) : null
                        }
                    >
                        <Box className={classes.container}>
                            <Box className={classes.content}>
                                {groups.map((group) => (
                                    <GroupFilterItem
                                        key={group.id}
                                        group={group}
                                        expandedGroups={expandedGroups}
                                        onExpand={onExpand}
                                        onChange={(name, value) => form.change(name, value)}
                                        onToggleGroup={createHierarchyToggle(allGroups, form)}
                                    />
                                ))}
                            </Box>
                        </Box>

                        <CheckboxField
                            label={t("common:button.selectAll")}
                            checked={values && Object.values(values).every((item) => item)}
                            indeterminate={false}
                            onChange={(e, checked) => onSelectAll(checked, form)}
                        />
                    </Modal>
                </form>
            )}
        />
    );
};
